import axios from '@/plugins/axios';

const AUTH_COOKIE = 'customer_logged_in';
const NAME_STORAGE_KEY = 'customer-display-name';

const hasAuthCookie = () => document.cookie.split('; ').some((c) => c.startsWith(`${AUTH_COOKIE}=`));

const authAreas = () => document.querySelectorAll('.js-auth-area');

/**
 * The name is cached so the inline header script can render it before the
 * first paint — the auth state request always arrives too late for that.
 */
const cachedName = () => {
    try {
        return window.localStorage.getItem(NAME_STORAGE_KEY) ?? '';
    } catch (e) {
        return '';
    }
};

const storeName = (state) => {
    try {
        if (state?.isAuthenticated) {
            window.localStorage.setItem(NAME_STORAGE_KEY, state.displayName || state.firstname || state.fullname || '');
        } else {
            window.localStorage.removeItem(NAME_STORAGE_KEY);
        }
    } catch (e) {
    }
};

const applyState = (area, state) => {
    const isAuthenticated = Boolean(state?.isAuthenticated);
    area.classList.toggle('logged-in', isAuthenticated);

    for (const nameEl of area.querySelectorAll('.js-auth-name')) {
        nameEl.textContent = isAuthenticated
            ? (state.displayName || state.firstname || state.fullname || '')
            : '';
    }

    // the trigger only has room for the shortened name, the open menu shows it whole
    for (const nameEl of area.querySelectorAll('.js-auth-fullname')) {
        nameEl.textContent = isAuthenticated
            ? (state.fullname || state.displayName || '')
            : '';
    }

    for (const el of area.querySelectorAll('.user-area-guest')) {
        el.style.display = isAuthenticated ? 'none' : '';
    }
    for (const el of area.querySelectorAll('.user-area-authenticated')) {
        el.style.display = isAuthenticated ? '' : 'none';
    }

    // the dropdown is rendered into the shared full page cache, so the per
    // customer counts arrive with the auth state and the sections that only
    // exist when they hold something stay hidden until then
    const counts = (isAuthenticated && state.navCounts) || {};
    const sections = (isAuthenticated && state.navSections) || {};
    const highlights = (isAuthenticated && state.navHighlights) || {};

    for (const el of area.querySelectorAll('.user-area-section')) {
        el.style.display = (sections[el.dataset.section] ?? ((counts[el.dataset.section] ?? 0) > 0)) ? '' : 'none';
    }

    for (const el of area.querySelectorAll('.user-area-count')) {
        const count = counts[el.dataset.countFor];
        el.textContent = count ? String(count) : '';
        el.toggleAttribute('hidden', !count);
        el.classList.toggle('user-area-count-highlight', Boolean(highlights[el.dataset.countFor]));
    }

    // the closed menu has to carry the same signal as the highlighted count
    // inside it, otherwise nothing tells the customer to open it. A plain dot
    // is enough here — the number itself waits inside the menu
    const flagged = Object.keys(highlights).some((section) => highlights[section]);

    for (const el of area.querySelectorAll('.user-area-badge')) {
        el.toggleAttribute('hidden', !flagged);
    }

    if (!isAuthenticated) {
        area.classList.remove('active');
    }
};

const bindInteractions = (area) => {
    for (const form of area.querySelectorAll('.user-area-logout-form')) {
        form.addEventListener('submit', async (e) => {
            const tokenInput = form.querySelector('input[name="_token"]');
            if (!tokenInput || tokenInput.value) {
                return;
            }
            e.preventDefault();
            try {
                const {data} = await axios.post(form.dataset.csrfUrl);
                tokenInput.value = typeof data === 'string' ? (data.match(/^[A-Za-z0-9]+/)?.[0] ?? '') : '';
            } catch (err) {
                console.error('CSRF refresh for logout failed', err);
            }
            form.submit();
        });
    }
};

/**
 * Projects can be switched on for a single customer from CP, so the answer is
 * per visitor while the pages carrying the UI are cached across all of them.
 * Both states ship in the markup and the effective one decides here.
 */
const applyProjectsVisibility = (state) => {
    const enabled = Boolean(state?.projectsEnabled);
    for (const el of document.querySelectorAll('[data-projects-only]')) {
        el.style.display = enabled ? '' : 'none';
    }
};

const setCustomerAuth = (state) => {
    window.customerAuth = state;
    storeName(state);
    for (const area of authAreas()) {
        applyState(area, state);
    }
    applyProjectsVisibility(state);
    document.dispatchEvent(new CustomEvent('customerAuthLoaded', {detail: state}));
};

const fetchAuthState = async (url) => {
    try {
        const {data} = await axios.get(url);
        if (data && typeof data.isAuthenticated === 'boolean') {
            return data;
        }
    } catch (e) {
        console.error('Auth state fetch failed', e);
    }
    return {isAuthenticated: false, email: null};
};

document.addEventListener('DOMContentLoaded', async () => {
    const areas = authAreas();
    if (!areas.length) {
        window.customerAuth = {isAuthenticated: false, email: null};
        document.dispatchEvent(new CustomEvent('customerAuthLoaded'));
        return;
    }

    for (const area of areas) {
        bindInteractions(area);
        // the inline header script already did this before the first paint;
        // repeated here for areas mounted later, with the cached name so the
        // rendered name is not wiped while the state request is in flight
        if (hasAuthCookie()) {
            applyState(area, {isAuthenticated: true, displayName: cachedName()});
        }
    }

    const stateUrl = areas[0].dataset.authStateUrl;
    setCustomerAuth(await fetchAuthState(stateUrl));

    // login/logout performed without a page reload
    document.addEventListener('customerAuthChanged', async () => {
        setCustomerAuth(await fetchAuthState(stateUrl));
    });

    document.addEventListener('click', (e) => {
        for (const area of document.querySelectorAll('.user-area.logged-in')) {
            if (!area.contains(e.target)) {
                area.classList.remove('active');
            }
        }
    });
});
